import { Image } from 'expo-image';
import React from 'react';
import { Pressable, StyleProp, View, ViewStyle } from 'react-native';

import {
  Button,
  IconCheck,
  IconClose,
  IconLockedOutline,
  IconStarRateFill,
  Markdown,
  Text,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { handleDeepLink } from '@fhs/utils/src/navigation';
import { Nullable } from '@rbi-ctg/frontend';

import { PointsBadge } from '../points-badge';

interface RewardTierCardProps {
  title: string;
  description?: Nullable<string>;
  image: string;
  pointsBadgeLabel: string;
  isRedeemable?: Nullable<boolean>;
  onPressRedeem?: () => void;
  isEligible?: Nullable<boolean>;
  learnMoreHref?: Nullable<string>;
  style?: StyleProp<ViewStyle>;
}

function EligibilityStatus({ isEligible }: { isEligible: boolean }) {
  const styles = useEligibilityStyles();
  return (
    <View
      style={[
        styles.statusContainer,
        { backgroundColor: isEligible ? tokens.colors.$success : tokens.colors.$error },
      ]}
    >
      {isEligible ? (
        <IconCheck color={tokens.colors.$white} />
      ) : (
        <IconClose color={tokens.colors.$white} />
      )}
      <Text.Ui size="sm" weight="semibold" style={styles.statusText}>
        {isEligible ? 'Eligible' : 'Ineligible'}
      </Text.Ui>
    </View>
  );
}

export function RewardTierCard({
  title,
  description,
  image,
  isRedeemable,
  onPressRedeem,
  isEligible,
  learnMoreHref,
  pointsBadgeLabel,
  style,
}: RewardTierCardProps) {
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  const styles = useMqStyles();
  return (
    <View style={[styles.container, style]}>
      <View style={styles.contentContainer}>
        <View style={styles.headerRow}>
          <View style={styles.imageContainer}>
            <Image contentFit="contain" source={image} style={styles.image} />
          </View>
          <View style={styles.textContainer}>
            {isDesktop && (
              <PointsBadge
                enabled={isRedeemable}
                label={pointsBadgeLabel}
                style={styles.pointsBadge}
              />
            )}
            <Text.Ui
              size="md"
              weight="semibold"
              style={{
                color: isRedeemable ? tokens.colors.$houseDark : tokens.colors.$disabledText,
              }}
            >
              {title}
            </Text.Ui>
            <View style={styles.descriptionRow}>
              {isRedeemable ? (
                <IconStarRateFill color={tokens.colors.$houseYellow} />
              ) : (
                <IconLockedOutline />
              )}
              {description && <Markdown style={styles.descriptionText}>{description}</Markdown>}
            </View>
          </View>
        </View>
        {(isRedeemable || isDesktop) && (
          <Button
            type="outline"
            style={styles.redeemButton}
            disabledStyle={styles.redeemButtonDisabled}
            onPress={onPressRedeem}
            disabled={!isRedeemable}
          >
            <Button.Text>Redeem</Button.Text>
          </Button>
        )}
      </View>
      {(learnMoreHref || typeof isEligible === 'boolean') && (
        <View style={styles.footerContainer}>
          {typeof isEligible === 'boolean' && <EligibilityStatus isEligible={isEligible} />}
          {learnMoreHref && (
            <Pressable style={styles.learnMoreLink} onPress={() => handleDeepLink(learnMoreHref)}>
              <Text.Ui size="sm" style={styles.learnMoreText}>
                Learn More
              </Text.Ui>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
}

const useEligibilityStyles = createMqStyles({
  statusContainer: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 4,
      backgroundColor: tokens.colors.$success,
    },
  },
  statusText: {
    $base: {
      color: tokens.colors.$white,
      marginLeft: 4,
    },
  },
});

const useMqStyles = createMqStyles({
  container: {
    $base: {
      paddingVertical: 12,
      borderWidth: 1,
      borderRadius: 8,
      borderColor: tokens.colors.$blackOpacity10,
    },
    $gteDesktop: {
      paddingVertical: 24,
    },
  },
  contentContainer: {
    $base: {
      paddingHorizontal: 16,
    },
    $gteDesktop: {
      flexDirection: 'row',
      paddingHorizontal: 28,
      flex: 1,
    },
  },
  headerRow: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
  },
  imageContainer: {
    $base: {
      backgroundColor: tokens.colors.$houseLight,
      borderRadius: 4,
      padding: 4,
    },
    $ltDesktop: {
      height: 46,
      width: 46,
    },
    $gteDesktop: {
      height: '100%',
      aspectRatio: 1,
      maxHeight: 70,
    },
  },
  image: {
    $base: {
      flex: 1,
    },
  },
  textContainer: {
    $base: {
      marginLeft: 12,
      flex: 1,
    },
  },
  pointsBadge: {
    $base: {
      marginBottom: 4,
    },
  },
  descriptionRow: {
    $base: {
      flexDirection: 'row',
      marginTop: 4,
      alignItems: 'center',
    },
  },
  descriptionText: {
    $base: {
      marginLeft: 4,
      flex: 1,
    },
  },
  redeemButton: {
    $ltDesktop: {
      marginTop: 12,
    },
    $gteDesktop: {
      alignSelf: 'center',
    },
  },
  redeemButtonDisabled: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
    },
  },
  footerContainer: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 12,
      paddingTop: 12,
      paddingHorizontal: 16,
      borderTopWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
    },
    $gteDesktop: {
      paddingHorizontal: 28,
      paddingTop: 24,
      marginTop: 24,
    },
  },
  learnMoreLink: {
    $base: {
      marginLeft: 12,
    },
  },
  learnMoreText: {
    $base: {
      textDecorationLine: 'underline',
    },
  },
});
